import React from 'react';
import { motion } from 'framer-motion';
import { Database, ArrowRight } from 'lucide-react';
interface SlideProps {
  index: number;
  total: number;
}
export function Slide7Stage2({
  index
}: SlideProps) {
  const personaStyles: Record<string, string> = {
    Unaware: 'bg-slate-100 text-slate-700',
    Aware: 'bg-purple-100 text-purple-800',
    Trialist: 'bg-amber-100 text-amber-800',
    'Frequent Prescriber': 'bg-blue-100 text-blue-800',
    'Clinical Expert': 'bg-[var(--brand-green-lighter)]/40 text-[var(--brand-green)]'
  };
  const mapData = [{
    content: 'Disease burden',
    onsite: 'Awareness explainer',
    outbound: 'Problem-led teaser',
    persona: 'Unaware'
  }, {
    content: 'Patient types',
    onsite: 'Patient identification module',
    outbound: 'Segment-specific email',
    persona: 'Aware'
  }, {
    content: 'Targeted treatment',
    onsite: 'Differentiation explainer',
    outbound: 'Innovation campaign',
    persona: 'Aware'
  }, {
    content: 'Efficacy',
    onsite: 'Confidence-building module',
    outbound: 'Trialist follow-up',
    persona: 'Trialist'
  }, {
    content: 'Safety',
    onsite: 'Reassurance module',
    outbound: 'Objection-handling campaign',
    persona: 'Trialist'
  }, {
    content: 'Application / dosing',
    onsite: 'Practical-use module',
    outbound: 'Rep-triggered follow-up',
    persona: 'Trialist'
  }, {
    content: 'Long-term data',
    onsite: 'Confidence module',
    outbound: 'Frequent prescriber campaign',
    persona: 'Frequent Prescriber'
  }, {
    content: 'Webinar / expert content',
    onsite: 'Event hub content',
    outbound: 'Pre/post-event Snaks',
    persona: 'Clinical Expert'
  }];
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)] p-[clamp(2rem,4vw,4rem)] flex flex-col" data-id="element-249">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(1.5rem,2.5vw,2.5rem)] flex-shrink-0 flex justify-between items-end" data-id="element-250">
        <div data-id="element-251">
          <p className="text-[var(--brand-green)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider mb-[clamp(0.5rem,1vw,1rem)]" data-id="element-252">
            Stage 2
          </p>
          <h2 className="text-[var(--fg)] font-bold text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.08] max-w-4xl" data-id="element-253">
            Turn Snakkified content into{' '}
            <span className="text-[var(--brand-green-light)]" data-id="element-254">
              campaign assets.
            </span>
          </h2>
        </div>
        <div className="hidden md:flex items-center gap-3 bg-[var(--surface)] px-4 py-2 rounded-full border border-black/5" data-id="element-255">
          <Database className="w-5 h-5 text-[var(--brand-green)]" data-id="element-256" />
          <span className="text-sm font-bold text-[var(--fg)]" data-id="element-257">
            Modular Reusability
          </span>
        </div>
      </motion.div>

      <div className="flex-1 flex flex-col min-h-0 gap-6" data-id="element-258">
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4,
        duration: 0.5
      }} className="text-[var(--fg)] text-[clamp(1.1rem,1.3vw,1.3rem)] font-medium max-w-4xl" data-id="element-259">
          Once the website has been turned into easy-to-digest Snak modules,
          those modules become reusable campaign assets. The website becomes
          more than a destination. It becomes the source of an always-on HCP
          engagement engine.
        </motion.p>

        <div className="flex-1 bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden flex flex-col min-h-0" data-id="element-260">
          <div className="grid grid-cols-4 bg-[var(--surface)] border-b border-black/5 p-4 px-8" data-id="element-261">
            <h3 className="text-[var(--muted)] font-bold text-sm uppercase tracking-wider" data-id="element-262">
              Website Content
            </h3>
            <h3 className="text-[var(--brand-green)] font-bold text-sm uppercase tracking-wider" data-id="element-263">
              On-site Snak Use
            </h3>
            <h3 className="text-[var(--brand-green-light)] font-bold text-sm uppercase tracking-wider" data-id="element-264">
              Outbound Campaign Use
            </h3>
            <h3 className="text-[var(--fg)] font-bold text-sm uppercase tracking-wider" data-id="element-265">
              Adoption Ladder Persona
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4 px-8 space-y-2" data-id="element-266">
            {mapData.map((row, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5 + idx * 0.05,
            duration: 0.3
          }} className="grid grid-cols-4 items-center py-3 border-b border-black/5 last:border-0 hover:bg-[var(--surface)]/50 rounded-lg px-4 transition-colors" data-id="element-267">
                <div className="font-medium text-[var(--fg)] flex items-center gap-4" data-id="element-268">
                  <div className="w-2 h-2 rounded-full bg-[var(--brand-green-lighter)]" data-id="element-269" />
                  {row.content}
                </div>
                <div className="font-bold text-[var(--brand-green)] flex items-center gap-3" data-id="element-270">
                  <ArrowRight className="w-4 h-4 text-[var(--muted)]" data-id="element-271" />
                  {row.onsite}
                </div>
                <div className="font-bold text-[var(--brand-green-light)] flex items-center gap-3" data-id="element-272">
                  <ArrowRight className="w-4 h-4 text-[var(--muted)]" data-id="element-273" />
                  {row.outbound}
                </div>
                <div className="flex items-center" data-id="element-274">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${personaStyles[row.persona]}`} data-id="element-275">
                    {row.persona}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </div>;
}